import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { loadReport, getCustomers, getSites, getHeatPumps } from "../lib/sync";
import { generateReportPDF } from "../lib/pdf";
import { getForm } from "../lib/formTemplates";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  ArrowLeft,
  Download,
  Share2,
  Copy,
  Mail,
  Pencil,
  Check,
  X,
  Link2,
} from "lucide-react";
import { toast } from "sonner";

export default function ReportView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [pump, setPump] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    const r = await loadReport(id).catch(() => null);
    if (!r) return;
    setReport(r);
    const [cs, sites, pumps] = await Promise.all([
      getCustomers().catch(() => []),
      getSites().catch(() => []),
      getHeatPumps().catch(() => []),
    ]);
    setCustomer(cs.find((c) => c.id === r.customer_id));
    setSite(sites.find((s) => s.id === r.site_id));
    setPump(pumps.find((p) => p.id === r.heat_pump_id));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!report) return <p className="py-10 text-center text-slate-400">Loading…</p>;

  const ctx = { report, customer, site, pump };
  const formDef = getForm(report.form_type);
  const shareUrl = report.share_id
    ? `${window.location.origin}/shared/${report.share_id}`
    : null;

  const download = async () => {
    setDownloading(true);
    try {
      await generateReportPDF(ctx);
    } catch (e) {
      toast.error("Could not generate PDF");
    }
    setDownloading(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied");
  };

  const emailLink = () => {
    const subject = encodeURIComponent(`Heat Pump Service Report — ${report.service_date}`);
    const body = encodeURIComponent(
      `Hi ${customer?.contact_person || ""},\n\nPlease find your heat pump service report here:\n${shareUrl}\n\nKind regards,\n${report.engineer_name}\nSterio Renewables`
    );
    window.location.href = `mailto:${customer?.email || ""}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="space-y-5 pb-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-semibold text-slate-500" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <Card className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-xl font-extrabold text-slate-900">
              {formDef.pdfTitle}
            </h1>
            <p className="text-sm text-slate-500">
              {report.service_date} • {report.engineer_name}
            </p>
          </div>
          <Badge className={report.status === "completed" ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-700"} variant="secondary">
            {report.status}
          </Badge>
        </div>
        {report.final_status && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary">
            Final status: {report.final_status}
          </div>
        )}
      </Card>

      {/* actions */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Button onClick={download} disabled={downloading} className="h-12" data-testid="download-pdf">
          <Download className="mr-1.5 h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" onClick={() => setShareOpen(true)} className="h-12" data-testid="share-btn">
          <Share2 className="mr-1.5 h-4 w-4" /> Share
        </Button>
        <Button variant="outline" onClick={() => navigate(`/report/new?duplicate=${report.id}`)} className="h-12" data-testid="duplicate-btn">
          <Copy className="mr-1.5 h-4 w-4" /> Duplicate
        </Button>
        <Button variant="outline" onClick={() => navigate(`/report/${report.id}/edit`)} className="h-12" data-testid="edit-btn">
          <Pencil className="mr-1.5 h-4 w-4" /> Edit
        </Button>
      </div>

      <DetailBlock title="Customer" rows={[["Name", customer?.name], ["Contact", customer?.contact_person], ["Email", customer?.email], ["Phone", customer?.phone]]} />
      <DetailBlock title="Site" rows={[["Site", site?.name], ["Address", site?.address]]} />
      <DetailBlock title="Heat pump" rows={[["Manufacturer", pump?.manufacturer], ["Model", pump?.model], ["Serial", pump?.serial_number], ["Refrigerant", pump?.refrigerant_type]]} />

      {formDef.sections.map((sec) => {
        const rows = sec.fields
          .map((f) => {
            const v = report.data?.[f.id];
            if (f.type === "check") return [f.label, v ? "Yes" : "No"];
            if (v === undefined || v === "" || v === null) return null;
            return [f.label + (f.unit ? ` (${f.unit})` : ""), String(v)];
          })
          .filter(Boolean);
        return <DetailBlock key={sec.id} title={sec.title} rows={rows} />;
      })}

      <DetailBlock
        title="Outcome"
        rows={[
          ["Engineer notes", report.data?.engineer_notes],
          ["Follow-up actions", report.follow_up_actions],
          ["Parts required", report.parts_required],
          ["Final status", report.final_status],
        ]}
      />

      {report.photos?.length > 0 && (
        <Card className="p-4">
          <h3 className="mb-2 font-heading text-sm font-bold text-primary">Photos</h3>
          <div className="grid grid-cols-3 gap-2">
            {report.photos.map((p) => (
              <img key={p.id} src={p.dataUrl} alt={p.name} className="aspect-square w-full rounded-lg object-cover" />
            ))}
          </div>
        </Card>
      )}

      <Card className="grid grid-cols-2 gap-4 p-4">
        <SigBlock label={`Engineer: ${report.engineer_name || ""}`} img={report.engineer_signature} />
        <SigBlock label="Customer" img={report.customer_signature} />
      </Card>

      {/* share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share report</DialogTitle>
          </DialogHeader>
          {report.status !== "completed" ? (
            <p className="text-sm text-amber-600">
              Complete the report first to generate a shareable link.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-slate-400" />
                <Input readOnly value={shareUrl} className="text-xs" data-testid="share-link-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={copyLink} className="h-11" data-testid="copy-link-btn">
                  <Copy className="mr-1.5 h-4 w-4" /> Copy link
                </Button>
                <Button variant="outline" onClick={emailLink} className="h-11" data-testid="email-link-btn">
                  <Mail className="mr-1.5 h-4 w-4" /> Email customer
                </Button>
              </div>
              <Button variant="outline" onClick={download} className="h-11 w-full" data-testid="share-download-pdf">
                <Download className="mr-1.5 h-4 w-4" /> Download PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailBlock({ title, rows }) {
  const visible = rows.filter(([, v]) => v != null && v !== "");
  if (visible.length === 0) return null;
  return (
    <Card className="p-4">
      <h3 className="mb-2 font-heading text-sm font-bold text-primary">{title}</h3>
      <dl className="space-y-1.5">
        {visible.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 border-b border-slate-100 py-1 text-sm">
            <dt className="text-slate-500">{k}</dt>
            <dd className="text-right font-medium text-slate-800">{v}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function SigBlock({ label, img }) {
  return (
    <div>
      <div className="flex h-20 items-center justify-center rounded-lg border border-slate-200 bg-white">
        {img ? (
          <img src={img} alt={label} className="h-full object-contain" />
        ) : (
          <X className="h-4 w-4 text-slate-300" />
        )}
      </div>
      <p className="mt-1 text-xs text-slate-500">{label}</p>
    </div>
  );
}
